import {
  $calloutMenu,
  CALLOUT_TYPES,
  type CalloutAttrs,
  type CalloutMenuState,
  type CalloutType,
  type Command,
  readCalloutAttrs,
} from '@bangle.io/prosemirror-plugins';
import {
  Button,
  Separator,
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@bangle.io/ui-components';
import { useAtomValue } from 'jotai';
import { ChevronsDownUp, X } from 'lucide-react';
import React from 'react';
import type { PmEditorService } from '../pm-editor-service';
import { useEditorCoreServices } from '../use-editor-core-services';
import {
  FLOATING_INITIAL_STYLE,
  useFloatingPosition,
} from './use-floating-position';

type EditorView = NonNullable<ReturnType<PmEditorService['getEditor']>>;
type Extensions = PmEditorService['extensions'];

/**
 * Floating toolbar for the callout the cursor is in. Lets the user change
 * type, set a custom title, and toggle the collapsed (`???`) form.
 *
 * Tip: click the callout's title in the note to jump straight into renaming.
 */
export function CalloutMenu({ editorName }: { editorName: string }) {
  const calloutMenus = useAtomValue($calloutMenu);
  const { editorEngine } = useEditorCoreServices();
  const editorView = editorEngine.getEditor(editorName);
  const calloutMenu = editorView ? calloutMenus.get(editorView) : undefined;

  if (!editorView || !calloutMenu?.show) {
    return null;
  }

  return (
    <CalloutMenuContent
      anchorEl={calloutMenu.anchorEl}
      calloutPos={calloutMenu.calloutPos}
      editorView={editorView}
      ext={editorEngine.extensions}
      focusTitle={Boolean(calloutMenu.focusTitle)}
      key={calloutMenu.calloutPos}
    />
  );
}

function CalloutMenuContent({
  anchorEl,
  calloutPos,
  editorView,
  ext,
  focusTitle,
}: {
  anchorEl: CalloutMenuState['anchorEl'];
  calloutPos: number;
  editorView: EditorView;
  ext: Extensions;
  focusTitle: boolean;
}) {
  const triggerRef = useFloatingPosition({
    show: true,
    anchorEl,
    boundaryElement: editorView.dom,
    placement: 'top-start',
  });
  const titleInputRef = React.useRef<HTMLInputElement>(null);

  const calloutNode = editorView.state.doc.nodeAt(calloutPos);
  const attrs: CalloutAttrs = calloutNode
    ? readCalloutAttrs(calloutNode.attrs)
    : { type: 'note', title: null, collapsed: false };

  const defaultTitle =
    t.app.editor.callout[attrs.type] ??
    `${attrs.type.charAt(0).toUpperCase()}${attrs.type.slice(1)}`;

  const [titleDraft, setTitleDraft] = React.useState(() => attrs.title ?? '');
  const [titleFocused, setTitleFocused] = React.useState(false);

  React.useEffect(() => {
    if (!titleFocused) {
      setTitleDraft(attrs.title ?? '');
    }
  }, [attrs.title, titleFocused]);

  React.useEffect(() => {
    if (!focusTitle) {
      return;
    }
    const input = titleInputRef.current;
    if (!input) {
      return;
    }
    input.focus();
    input.select();
    ext.calloutMenu.command.clearCalloutTitleFocus()(
      editorView.state,
      undefined,
      editorView,
    );
  }, [editorView, ext, focusTitle]);

  const run = (command: Command, { refocus = true } = {}) => {
    command(editorView.state, editorView.dispatch, editorView);
    if (refocus) {
      editorView.focus();
    }
  };

  const dismiss = () => {
    run(ext.calloutMenu.command.dismissCalloutMenu());
  };

  const commitTitle = (raw: string) => {
    const trimmed = raw.trim();
    const nextTitle = trimmed === '' ? null : trimmed;
    if (nextTitle === attrs.title) {
      setTitleDraft(attrs.title ?? '');
      return;
    }
    // Keep focus in the title field's blur cycle; run() would yank it back
    // into the editor before the browser finishes the blur.
    run(ext.callout.command.setCalloutAttrs({ title: nextTitle }), {
      refocus: false,
    });
    editorView.focus();
  };

  return (
    <div ref={triggerRef} style={FLOATING_INITIAL_STYLE}>
      <div
        aria-label={t.app.editor.calloutMenu.label}
        className="flex flex-wrap items-center gap-1 rounded-md border bg-popover p-1 shadow-xs"
        data-testid="callout-menu"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            dismiss();
          }
        }}
        role="toolbar"
      >
        <TooltipProvider delay={300}>
          <label className="flex items-center gap-1 px-0.5">
            <span className="sr-only">{t.app.editor.calloutMenu.type}</span>
            <select
              aria-label={t.app.editor.calloutMenu.type}
              className="h-8 max-w-32 rounded-md border border-input bg-background px-1.5 text-xs outline-none focus-visible:ring-1 focus-visible:ring-ring"
              onChange={(event) => {
                const next = event.target.value as CalloutType;
                run(ext.callout.command.setCalloutAttrs({ type: next }));
              }}
              onPointerDown={(event) => event.stopPropagation()}
              value={attrs.type}
            >
              {CALLOUT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t.app.editor.callout[type]}
                </option>
              ))}
            </select>
          </label>

          <Separator className="mx-0.5 h-6" orientation="vertical" />

          <label className="flex items-center gap-1 px-0.5">
            <span className="sr-only">{t.app.editor.calloutMenu.title}</span>
            <input
              ref={titleInputRef}
              aria-label={t.app.editor.calloutMenu.title}
              className="h-8 w-36 rounded-md border border-input bg-background px-2 text-xs outline-none focus-visible:ring-1 focus-visible:ring-ring"
              onBlur={() => {
                setTitleFocused(false);
                commitTitle(titleDraft);
              }}
              onChange={(event) => setTitleDraft(event.target.value)}
              onFocus={() => setTitleFocused(true)}
              onKeyDown={(event) => {
                // Keep keystrokes out of the editor / other floating menus.
                event.stopPropagation();
                if (event.key === 'Enter') {
                  event.preventDefault();
                  (event.target as HTMLInputElement).blur();
                }
              }}
              onPointerDown={(event) => event.stopPropagation()}
              placeholder={defaultTitle}
              title={t.app.editor.calloutMenu.titleHint}
              type="text"
              value={titleDraft}
            />
          </label>

          <Separator className="mx-0.5 h-6" orientation="vertical" />

          <Tooltip>
            <TooltipTrigger
              render={
                <Toggle
                  aria-label={t.app.editor.calloutMenu.collapsed}
                  aria-pressed={attrs.collapsed}
                  className="h-8 w-8 p-0"
                  onPointerDown={(event) => event.preventDefault()}
                  onPressedChange={() => {
                    run(
                      ext.callout.command.setCalloutAttrs({
                        collapsed: !attrs.collapsed,
                      }),
                    );
                  }}
                  pressed={attrs.collapsed}
                  size="sm"
                >
                  <ChevronsDownUp />
                </Toggle>
              }
            />
            <TooltipContent side="bottom" className="text-xs">
              {t.app.editor.calloutMenu.collapsedHint}
            </TooltipContent>
          </Tooltip>

          <Button
            aria-label={t.app.common.dismiss}
            className="h-8 w-8 p-0"
            onClick={dismiss}
            onPointerDown={(event) => event.preventDefault()}
            size="icon"
            type="button"
            variant="ghost"
          >
            <X />
          </Button>
        </TooltipProvider>
      </div>
    </div>
  );
}
